import { useState } from "react";

import { useMyUsage } from "../../api/queries";
import type { MyUsage } from "../../api/types";
import { formatGpuHours } from "../../lib/format";
import { ErrorBlock, LoadingBlock } from "../ui/StateBlock";

/** The windows a person may look back over, in days. */
export const USAGE_PERIODS = [7, 30, 90] as const;

/**
 * Format a euro amount for a sentence, e.g. `€383`, `€0.50`.
 *
 * Whole euros above ten — a person reading their month does not need the
 * cents — and two decimals below, where the cents are the number.
 */
export function formatEuros(value: number): string {
  const digits = Math.abs(value) >= 10 ? 0 : 2;
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}

/**
 * The sentences that tell a person what they held and what it cost.
 *
 * Plain prose rather than a table, on purpose: the point is not to account
 * for the hours but to make the idle share felt. Every sentence that names
 * money depends on a declared rate — with none, the money is not mentioned,
 * never guessed.
 *
 * Args:
 *     usage: The server's figures for the chosen window.
 *
 * Returns:
 *     The sentences, in reading order.
 */
export function usageSentences(usage: MyUsage): string[] {
  const window = `Over the last ${usage.days} days`;
  const sentences: string[] = [];
  if (usage.gpu_hours === 0 && usage.cpu_core_hours === 0) {
    sentences.push(`${window} you held no GPU and no CPU core.`);
    return sentences;
  }
  const held = `${formatGpuHours(usage.gpu_hours)} GPU-hours`;
  if (usage.gpu_hours === 0) {
    sentences.push(`${window} you held no GPU.`);
  } else if (usage.busy_gpu_hours === null || usage.idle_gpu_hours === null) {
    sentences.push(
      `${window} you held ${held}. How much of that was computing has only ` +
        "been measured recently, so it cannot be said for this window yet.",
    );
  } else {
    sentences.push(
      `${window} you held ${held}: ${formatGpuHours(usage.busy_gpu_hours)} ` +
        `of them were computing, ${formatGpuHours(usage.idle_gpu_hours)} sat idle.`,
    );
  }
  if (usage.cpu_core_hours > 0) {
    sentences.push(
      `You also held ${formatGpuHours(usage.cpu_core_hours)} CPU core-hours.`,
    );
  }
  const rate = usage.prices.gpu_hour_eur;
  const gpuCost = usage.cost.gpu_eur;
  if (gpuCost !== null && usage.gpu_hours > 0) {
    const at =
      rate !== null
        ? `At ${formatEuros(rate)} per GPU-hour that is about ${formatEuros(gpuCost)}`
        : `Priced per GPU family, that is about ${formatEuros(gpuCost)}`;
    const idle =
      usage.cost.idle_gpu_eur !== null
        ? `, of which about ${formatEuros(usage.cost.idle_gpu_eur)} for GPUs ` +
          "that were held but not computing."
        : ".";
    sentences.push(at + idle);
  }
  if (usage.gpu_hours > 0) {
    sentences.push(
      "Idle GPU time is the one number worth lowering: stop a workspace when " +
        "you step away, or ask for fewer GPUs — the console never refuses you " +
        "more later.",
    );
  }
  return sentences;
}

/**
 * "Your usage": what you held over a window, and what it cost.
 *
 * Information for the person reading it, never a quota (ADR-0008, ADR-0025):
 * the last line says so, because a number about money next to a name reads
 * as a budget unless the screen says it is not one.
 */
export function UsageSection(): JSX.Element {
  const [days, setDays] = useState<(typeof USAGE_PERIODS)[number]>(30);
  const usage = useMyUsage(days);

  return (
    <>
      <p className="section__meta" role="group" aria-label="Period">
        {USAGE_PERIODS.map((period) => (
          <button
            key={period}
            type="button"
            className="btn btn--xs"
            aria-pressed={period === days}
            onClick={() => setDays(period)}
          >
            {period} days
          </button>
        ))}
      </p>
      {usage.isPending ? (
        <LoadingBlock label="Adding up your hours…" />
      ) : usage.isError ? (
        <ErrorBlock error={usage.error} onRetry={() => void usage.refetch()} />
      ) : (
        <div className="usage-story">
          {usageSentences(usage.data).map((sentence) => (
            <p key={sentence}>{sentence}</p>
          ))}
          <p className="aside__meta">
            This is information, nothing more: the console never refuses you
            GPUs because of it. GPU-hours are what you held, from the
            console&apos;s own record; computing time is read from the GPU
            telemetry since the console started counting.
          </p>
        </div>
      )}
    </>
  );
}
